import { IGeneratedSecrReportResponse } from '../../../../shared/services/generated-report/generated-report.model';
import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Alert, Chip, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { ReportingSecrTableHeading } from './reporting-secr-table-heading';
import { ReportingDataQualityMix } from '../reporting-data-quality-mix';
import { ReportingReductionDisclosures } from '../reporting-reduction-disclosures';
import { ReportingSecrTableRow } from './reporting-secr-table-row';
import { ReportingSecrSourceMatrix } from './reporting-secr-source-matrix';
import {
  emptyEmissionData,
  hasScopeThreeData,
  logSecrSummaryValidationDev,
  reportHasAnyScopeThreeFigures,
  SECR_REPORT_LABEL_COLUMN_PERCENT,
  validateSecrSummaryTotals,
} from './secr-summary-table.utils';
import IntensityRatioService from '../../../../shared/services/intensity-ratio/intensity-ratio.service';
import { IIntensityRatio } from '../../../../shared/services/intensity-ratio/intensity-ratio.model';

const intensityRatioService = new IntensityRatioService();

/** showSignOff: the sign-off block is print-only, so only the PDF-route wrapper passes true.
 * hideNarratives: the report page shows (and edits) the two narratives above the table, so it passes
 * true to avoid printing them twice; the PDF-route wrapper leaves them where they have always been. */
export const ReportingSecrTable: FunctionComponent<{ report: IGeneratedSecrReportResponse, showSignOff?: boolean, hideNarratives?: boolean }> = ({ report, showSignOff = false, hideNarratives = false }) => {
  const primaryDetail = report.reportDetails.find((value) => value.isPrimaryReportPeriod);
  const sourceChecks = primaryDetail?.secrSourceChecks;
  const [checksDismissed, setChecksDismissed] = useState(false);
  const showSourceSection =
    report.includeEnergyCategoryBreakdown !== false &&
    report.reportDetails.some((d) => (d.energyBreakdownBySource?.length ?? 0) > 0);

  const comparisonPeriods = useMemo(
    () =>
      report.reportDetails
        .filter((v) => !v.isPrimaryReportPeriod)
        .sort((a, b) => Number(b.reportPeriodName) - Number(a.reportPeriodName)),
    [report.reportDetails]
  );

  const showScopeThreeRow = report.includeScopeThree && reportHasAnyScopeThreeFigures(report);
  // TICKET-E: only show the biogenic memo row if any period actually has biogenic CO2 outside of scopes.
  const showBiogenicRow = report.reportDetails.some((d) => {
    const b = d.emissions?.biogenic;
    return !!b && ((b.uk ?? 0) !== 0 || (b.overseas ?? 0) !== 0 || (b.global ?? 0) !== 0);
  });

  useEffect(() => {
    for (const d of report.reportDetails) {
      const includeS3InSum = report.includeScopeThree && hasScopeThreeData(d.emissions?.scopeThree);
      const result = validateSecrSummaryTotals(d, includeS3InSum);
      logSecrSummaryValidationDev(d.reportPeriodName, result);
    }
  }, [report.reportDetails, report.includeScopeThree]);

  const colsPerPeriod = report.includeGlobal ? 3 : 1;
  const dataColumnCount = report.reportDetails.length * colsPerPeriod;
  const labelColumnPercent = SECR_REPORT_LABEL_COLUMN_PERCENT;
  const dataColumnPercent = dataColumnCount > 0 ? (100 - labelColumnPercent) / dataColumnCount : 100 - labelColumnPercent;

  const primary = report.reportDetails.find((v) => v.isPrimaryReportPeriod);

  // Resolve stored ratio keys to their central definitions for labelling. getAll returns
  // all 4 catalog entries regardless of enabled state, so historical keys still resolve.
  const [ratioDefinitions, setRatioDefinitions] = useState<IIntensityRatio[]>([]);
  useEffect(() => {
    intensityRatioService.getAll()
      .then(setRatioDefinitions)
      .catch((err) => console.log(err));
  }, []);
  const ratioDefByKey = useMemo(
    () => new Map(ratioDefinitions.map((d) => [d.key, d])),
    [ratioDefinitions]
  );

  const ratioTitle = (def: IIntensityRatio) => {
    const scopeSuffix = def.includesScopeThree ? '(total)' : '(Scope 1 & 2)';
    if (def.denominatorType === 'revenue') {
      return (<>tCO<sub>2</sub>e per {report.currencySymbol}m revenue {scopeSuffix}</>);
    }
    if (def.denominatorType === 'floorArea') {
      return (<>tCO<sub>2</sub>e per m<sup>2</sup> floor area {scopeSuffix}</>);
    }
    return (<>tCO<sub>2</sub>e per employee {scopeSuffix}</>);
  };

  const intensityBlock =
    report.intensityRatios.length > 0 ? (
      <>
        <ReportingSecrTableHeading report={report} title="Intensity ratios (Location-based)" />
        {report.intensityRatios.map((key) => {
          const def = ratioDefByKey.get(key);
          if (!def) return null;
          return (
            <ReportingSecrTableRow
              key={key}
              title={ratioTitle(def)}
              primaryData={primary?.intensityRatios.find((v) => v.intensityRatio === key)?.emissions ?? emptyEmissionData()}
              comparisonData={comparisonPeriods.map(
                (r) => r.intensityRatios.find((v) => v.intensityRatio === key)?.emissions ?? emptyEmissionData()
              )}
              showOverseas={!!report.includeGlobal}
            />
          );
        })}
      </>
    ) : null;

  return (
    <Box
      sx={{
        mb: 2,
        overflowX: 'auto',
        maxWidth: '100%',
      }}
    >
      {/* TICKET-G2: provisional until the report is marked complete (locked). Subtle outlined styling. */}
      {report.isProvisional !== undefined && (
        <Chip
          size="small"
          variant="outlined"
          label={report.isProvisional ? 'Provisional' : 'Locked'}
          color={report.isProvisional ? 'default' : 'success'}
          sx={{ mb: 1.5, fontWeight: 500, color: 'text.secondary' }}
        />
      )}
      {/* TICKET-H3 §S5: SECR low-energy-user exemption (primary period < 40,000 kWh global). */}
      {primary?.isLowEnergyUser && (
        <Chip
          size="small"
          label="Low energy user (SECR exemption may apply)"
          color="info"
          sx={{ mb: 1.5, ml: 1, fontWeight: 600 }}
        />
      )}
      <TableContainer>
        <Table
          size="small"
          sx={{
            tableLayout: 'fixed',
            width: '100%',
            '& tbody .MuiTableCell-root': { py: 0.55 },
          }}
        >
          <colgroup>
            <col style={{ width: `${labelColumnPercent}%` }} />
            {Array.from({ length: dataColumnCount }).map((_, i) => (
              <col key={i} style={{ width: `${dataColumnPercent}%` }} />
            ))}
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell colSpan={1} sx={{ borderBottom: 'none' }}></TableCell>
              <TableCell colSpan={report?.includeGlobal ? 3 : 1} align="center" sx={{ borderBottom: 'none' }}>
                <Chip
                  label={`${report.reportDetails.find((value) => value.isPrimaryReportPeriod)?.reportPeriodName} - Current Year`}
                  sx={{ fontWeight: 600, fontSize: '1.15rem', color: 'primary.dark', backgroundColor: 'secondary.light' }}
                />
              </TableCell>
              {comparisonPeriods.map((value, index) => {
                return (
                  <TableCell key={index} colSpan={report.includeGlobal ? 3 : 1} align="center" sx={{ borderBottom: 'none' }}>
                    <Chip
                      label={`${value.reportPeriodName}${value.isBaseYearReportPeriod ? ' - Base Year' : ''}`}
                      sx={{ fontWeight: 600, fontSize: '1.15rem', color: 'common.black' }}
                    />
                  </TableCell>
                );
              })}
            </TableRow>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 600,
                  fontSize: '1rem',
                  color: 'common.black',
                  borderBottomColor: 'primary.dark',
                  borderBottomWidth: '3px',
                  borderBottomStyle: 'solid',
                }}
              ></TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: 600,
                  fontSize: '1rem',
                  color: 'common.black',
                  borderBottomColor: 'primary.dark',
                  borderBottomWidth: '3px',
                  borderBottomStyle: 'solid',
                }}
              >
                UK
              </TableCell>
              {report.includeGlobal && (
                <>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 600,
                      fontSize: '1rem',
                      color: 'common.black',
                      borderBottomColor: 'primary.dark',
                      borderBottomWidth: '3px',
                      borderBottomStyle: 'solid',
                    }}
                  >
                    Overseas
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 600,
                      fontSize: '1rem',
                      color: 'common.black',
                      borderBottomColor: 'primary.dark',
                      borderBottomWidth: '3px',
                      borderBottomStyle: 'solid',
                    }}
                  >
                    Total
                  </TableCell>
                </>
              )}
              {comparisonPeriods.map((value, index) => {
                return (
                  <React.Fragment key={value.reportPeriodId}>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: 600,
                        fontSize: '1rem',
                        color: 'common.black',
                        borderBottomColor: 'primary.dark',
                        borderBottomWidth: '3px',
                        borderBottomStyle: 'solid',
                      }}
                    >
                      UK
                    </TableCell>
                    {report.includeGlobal && (
                      <>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 600,
                            fontSize: '1rem',
                            color: 'common.black',
                            borderBottomColor: 'primary.dark',
                            borderBottomWidth: '3px',
                            borderBottomStyle: 'solid',
                          }}
                        >
                          Overseas
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 600,
                            fontSize: '1rem',
                            color: 'common.black',
                            borderBottomColor: 'primary.dark',
                            borderBottomWidth: '3px',
                            borderBottomStyle: 'solid',
                          }}
                        >
                          Total
                        </TableCell>
                      </>
                    )}
                  </React.Fragment>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            <ReportingSecrTableHeading report={report} title="Emissions" includeTopBorder={false} />
            <ReportingSecrTableRow
              title={<>Scope 1 (tCO<sub>2</sub>e)</>}
              primaryData={primary?.emissions.scopeOne}
              comparisonData={comparisonPeriods.map((r) => r.emissions.scopeOne)}
              showOverseas={!!report.includeGlobal}
            />
            <ReportingSecrTableRow
              title={<>Scope 2 (Location-based) (tCO<sub>2</sub>e)</>}
              primaryData={primary?.emissions.scopeTwoLocation}
              comparisonData={comparisonPeriods.map((r) => r.emissions.scopeTwoLocation)}
              showOverseas={!!report.includeGlobal}
            />
            <ReportingSecrTableRow
              title={<>Scope 2 (Market-based) (tCO<sub>2</sub>e)</>}
              primaryData={primary?.emissions.scopeTwoMarket}
              comparisonData={comparisonPeriods.map((r) => r.emissions.scopeTwoMarket)}
              showOverseas={!!report.includeGlobal}
            />
            {showScopeThreeRow && (
              <ReportingSecrTableRow
                title={<>Scope 3 (tCO<sub>2</sub>e)</>}
                primaryData={primary?.emissions.scopeThree}
                comparisonData={comparisonPeriods.map((r) => r.emissions.scopeThree)}
                showOverseas={!!report.includeGlobal}
              />
            )}
            <ReportingSecrTableRow
              title={<>Total emissions (Location-based) (tCO<sub>2</sub>e)</>}
              primaryData={primary?.emissions.totalLocation}
              comparisonData={comparisonPeriods.map((r) => r.emissions.totalLocation)}
              showOverseas={!!report.includeGlobal}
            />
            <ReportingSecrTableRow
              title={<>Total emissions (Market-based) (tCO<sub>2</sub>e)</>}
              primaryData={primary?.emissions.totalMarket}
              comparisonData={comparisonPeriods.map((r) => r.emissions.totalMarket)}
              showOverseas={!!report.includeGlobal}
            />
            {/* TICKET-E: biogenic CO2 memo row — GHG Protocol "reported separately from the scopes". This sits
                below the totals and is NOT included in any scope or the totals above. */}
            {showBiogenicRow && (
              <ReportingSecrTableRow
                title={<>Biogenic CO<sub>2</sub> (outside of scopes) (tCO<sub>2</sub>e)</>}
                primaryData={primary?.emissions.biogenic ?? emptyEmissionData()}
                comparisonData={comparisonPeriods.map((r) => r.emissions.biogenic ?? emptyEmissionData())}
                showOverseas={!!report.includeGlobal}
              />
            )}
            {intensityBlock}
            <ReportingSecrTableHeading report={report} title="Energy consumption" />
            <ReportingSecrTableRow
              title={<>Energy consumption (kWh)</>}
              primaryData={primary?.kwh}
              comparisonData={comparisonPeriods.map((r) => r.kwh)}
              showOverseas={!!report.includeGlobal}
            />
          </TableBody>
        </Table>
      </TableContainer>
      {showSourceSection && (
        <>
          <Typography
            variant="subtitle1"
            sx={{ mt: 4, mb: 1.5, fontWeight: 600, color: 'common.black', fontSize: '1.125rem' }}
          >
            Energy and emissions by source (Scope 1 &amp; 2)
          </Typography>
          {(sourceChecks?.warnings?.length ?? 0) > 0 && !checksDismissed && (
            <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setChecksDismissed(true)}>
              {(sourceChecks?.warnings ?? []).map((w, i) => (
                <div key={i}>{w}</div>
              ))}
            </Alert>
          )}
          <ReportingSecrSourceMatrix report={report} />
          {/* TICKET-H4 §S3/§S4: the energy-by-source reconciliation + unmapped-share figures remain on the
              report payload (energyBySourceSummary) as an internal data-quality signal, but are intentionally
              NOT rendered as a customer-facing warning — it is not customer-actionable. */}
        </>
      )}
      {/* TICKET-H3 §S1: flag intensity ratios omitted because a denominator (revenue/employees) was missing. */}
      {(primary?.intensityRatios?.some((r) => r.denominatorMissing) ?? false) && (
        <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
          Some intensity ratios are shown as n/a because the required denominator (revenue or employee count) was
          not provided for the period.
        </Typography>
      )}
      {/* TICKET-C: GHG Protocol organisational-boundary consolidation approach (required disclosure). */}
      {report.consolidationApproach && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 600, color: 'common.black', fontSize: '1.125rem' }}>
            Organisational boundary
          </Typography>
          <Typography variant="body2" sx={{ color: 'common.black' }}>
            Consolidation approach: {{
              EQUITY_SHARE: 'Equity share',
              FINANCIAL_CONTROL: 'Financial control',
              OPERATIONAL_CONTROL: 'Operational control',
            }[report.consolidationApproach]}
          </Typography>
        </Box>
      )}
      {/* §S2 (TICKET-H3): statutory SECR narrative disclosures (each its own section for consistent spacing). */}
      {!hideNarratives && report.methodologyStatement && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 600, color: 'common.black', fontSize: '1.125rem' }}>
            Methodology
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'common.black' }}>
            {report.methodologyStatement}
          </Typography>
        </Box>
      )}
      {!hideNarratives && report.energyEfficiencyNarrative && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 600, color: 'common.black', fontSize: '1.125rem' }}>
            Energy efficiency action taken
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'common.black' }}>
            {report.energyEfficiencyNarrative}
          </Typography>
        </Box>
      )}
      {/* Supplementary annex: data-quality mix for the primary period (outside the statutory table). */}
      {report.dataQualityMix && (report.dataQualityMix.totalLocation?.totalTco2e > 0 || report.dataQualityMix.totalMarket?.totalTco2e > 0) && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: 'common.black', fontSize: '1.125rem' }}>
            Data quality
          </Typography>
          <ReportingDataQualityMix mix={report.dataQualityMix} />
        </Box>
      )}
      {/* Supplementary annex: assumptions recorded against the primary period. */}
      {(report.assumptionsRegister?.length ?? 0) > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: 'common.black', fontSize: '1.125rem' }}>
            Assumptions
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Assumption</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Group</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(report.assumptionsRegister ?? []).map((assumption, index) => (
                  <TableRow key={index}>
                    <TableCell>{assumption.name}</TableCell>
                    <TableCell sx={{ whiteSpace: 'pre-wrap' }}>
                      {assumption.description}
                      {/* Beneath the description, as the UK SRS report shows it. */}
                      {assumption.additionalInformation && (
                        <Typography variant="body2" component="span" sx={{ display: 'block', color: 'text.secondary' }}>
                          {assumption.additionalInformation}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{assumption.status}</TableCell>
                    <TableCell>{assumption.groupName || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
      {/* TICKET-D-a: GHG Protocol disclosure — GWP basis. (Biogenic CO2 is now a memo row in the table above.) */}
      {(report.gwpVersions?.length ?? 0) > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 600, color: 'common.black', fontSize: '1.125rem' }}>
            Greenhouse gas disclosures
          </Typography>
          <Typography variant="body2" sx={{ color: 'common.black' }}>
            GWP basis: {(report.gwpVersions ?? []).join(', ')}
          </Typography>
        </Box>
      )}
      {/* Supplementary annex: reduction targets, plan coverage and the offsets register.
          Outside the statutory table; statutory figures stay gross. */}
      {report.reductionDisclosures && (
        <ReportingReductionDisclosures disclosures={report.reductionDisclosures} />
      )}
      {/* Print-only sign-off: rendered on the PDF route only (showSignOff), and only when a
          name was captured at generation. */}
      {showSignOff && report.signOffName && (
        <Box sx={{ mt: 4, breakInside: 'avoid' }}>
          <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 600, color: 'common.black', fontSize: '1.125rem' }}>
            Sign-off
          </Typography>
          <Typography variant="body2" sx={{ color: 'common.black' }}>
            <strong>Signed on behalf of the company:</strong>
          </Typography>
          <Typography variant="body2" sx={{ color: 'common.black' }}>
            {report.signOffName}{report.signOffTitle ? `, ${report.signOffTitle}` : ''}
          </Typography>
          {report.signOffDate && (
            <Typography variant="body2" sx={{ color: 'common.black', mt: 1 }}>
              <strong>Date:</strong> {moment(report.signOffDate).format('D MMMM YYYY')}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};
